import { api } from './api';

export interface Company {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  logo: string | null;
  gstin: string | null;
  stateCode: string | null;
  countryCode?: string | null;
  timezone?: string | null;
  jobSheetInstructions: string | null;
  invoiceTermsText: string | null;
  estimationTermsText: string | null;
  allowMultipleSettlements: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CashSettlementSettings {
  allowMultipleSettlements: boolean;
}

export interface DeliveryDueSettings {
  deliveryDueMode: 'off' | 'warn' | 'strict';
  deliveryDueLimit: number;
  deliveryDueOverrideRoles: string[];
}

export interface RepeatedServiceSettings {
  repeatedServiceDaysWindow: number;
  repeatedServiceRequireFaultMatch: boolean;
}

export interface UpdateCompanyData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  gstin?: string;
  stateCode?: string;
  timezone?: string;
  jobSheetInstructions?: string;
  invoiceTermsText?: string;
  estimationTermsText?: string;
}

export const companyApi = {
  /**
   * Get current company details
   */
  getCompany: async (): Promise<Company> => {
    const response = await api.get('/company');
    return response.data.data;
  },

  /**
   * Update company details
   */
  updateCompany: async (data: UpdateCompanyData): Promise<Company> => {
    const response = await api.put('/company', data);
    return response.data.data;
  },

  /**
   * Upload company logo
   */
  uploadLogo: async (file: File): Promise<Company> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await api.post('/company/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  /**
   * Delete company logo
   */
  deleteLogo: async (): Promise<Company> => {
    const response = await api.delete('/company/logo');
    return response.data.data;
  },

  /**
   * Get cash settlement settings
   */
  getCashSettlementSettings: async (): Promise<CashSettlementSettings> => {
    const response = await api.get('/company/settings/cash-settlement');
    return response.data.data;
  },

  /**
   * Update cash settlement settings
   */
  updateCashSettlementSettings: async (settings: CashSettlementSettings): Promise<CashSettlementSettings> => {
    const response = await api.put('/company/settings/cash-settlement', settings);
    return response.data.data;
  },

  /**
   * Get delivery due control settings
   */
  getDeliveryDueSettings: async (): Promise<DeliveryDueSettings> => {
    const response = await api.get('/company/settings/delivery-due');
    return response.data.data;
  },

  /**
   * Update delivery due control settings
   */
  updateDeliveryDueSettings: async (settings: DeliveryDueSettings): Promise<DeliveryDueSettings> => {
    const response = await api.put('/company/settings/delivery-due', settings);
    return response.data.data;
  },

  /**
   * Get delivery OTP settings
   */
  getDeliveryOtpSettings: async (): Promise<{ deliveryOtpRequired: boolean }> => {
    const response = await api.get('/company/settings/delivery-otp');
    return response.data.data;
  },

  /**
   * Update delivery OTP settings
   */
  updateDeliveryOtpSettings: async (settings: { deliveryOtpRequired: boolean }): Promise<{ deliveryOtpRequired: boolean }> => {
    const response = await api.put('/company/settings/delivery-otp', settings);
    return response.data.data;
  },

  /**
   * Get repeated service detection settings
   */
  getRepeatedServiceSettings: async (): Promise<RepeatedServiceSettings> => {
    const response = await api.get('/company/settings/repeated-service');
    return response.data.data;
  },

  /**
   * Update repeated service detection settings
   */
  updateRepeatedServiceSettings: async (settings: RepeatedServiceSettings): Promise<RepeatedServiceSettings> => {
    const response = await api.put('/company/settings/repeated-service', settings);
    return response.data.data;
  },
};
